import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import DreamIcon from '../common/DreamMarque';
import { ReactComponent as DreamWordmark } from '../../icons/brand/dream-wordmark.svg';
import { ReactComponent as MemoryIcon } from '../../icons/nav-links/memory.svg';
import { ReactComponent as SettingsIcon } from '../../icons/nav-links/settings.svg';
import { ReactComponent as HomeIcon } from '../../icons/nav-links/home.svg';
import { ReactComponent as LogoutIcon } from '../../icons/nav-links/logout.svg';
import { ReactComponent as WhatsappIcon } from '../../icons/whatsapp-color.svg';
import { useMobileNav } from '../../contexts/MobileNavContext';
import { useAgentWhatsappNumber } from '../../hooks/useAgentWhatsappNumber';
import { settingsLinkState } from '../../utils/settingsReturn';
import './uni-nav.scss';

export type UniTab = 'home' | 'activity' | 'reminders' | 'documents';

export type UniNavMode = 'default' | 'artifact';

interface UniNavProps {
  activeTab?: UniTab;
  onTabChange?: (tab: UniTab) => void;
  activityCount?: number;
  remindersCount?: number;
  scrolled?: boolean;
  mode?: UniNavMode;
  agentWhatsappNumber?: string | null;
  /** If true, hides user-specific actions (Settings, WhatsApp) for public share pages. */
  isPublic?: boolean;
}

const NewWindowLink: React.FC<{
  href: string;
  className?: string;
  'aria-label'?: string;
  children: React.ReactNode;
}> = ({ href, className, 'aria-label': ariaLabel, children }) => (
  <a
    href={href}
    target='_blank'
    rel='noreferrer'
    className={className}
    aria-label={ariaLabel}
  >
    {children}
  </a>
);

const UniNav: React.FC<UniNavProps> = ({
  scrolled,
  mode = 'default',
  agentWhatsappNumber,
  isPublic = false,
}) => {
  const { logout } = useAuth0();
  const { isMobile } = useMobileNav();
  const location = useLocation();
  const { whatsappLink, agentWhatsappLink: hookAgentWhatsappLink } =
    useAgentWhatsappNumber();

  const agentWhatsappLink = agentWhatsappNumber
    ? `https://wa.me/${agentWhatsappNumber.replace(/\D/g, '')}`
    : hookAgentWhatsappLink;

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const isArtifact = mode === 'artifact';
  const homeHref = isPublic ? 'https://usedream.ai/' : '/';
  const logo = (
    <>
      <DreamIcon />
      <DreamWordmark className='uni-nav-wordmark' />
    </>
  );

  return (
    <div className={`uni-nav${scrolled ? ' scrolled' : ''}`}>
      <div className='uni-nav-left'>
        {isArtifact && isMobile ? (
          <NewWindowLink
            href={homeHref}
            className='uni-nav-logo'
            aria-label='Home'
          >
            <DreamIcon />
          </NewWindowLink>
        ) : isArtifact ? (
          <NewWindowLink
            href={homeHref}
            className='uni-nav-logo'
            aria-label='Home'
          >
            {logo}
          </NewWindowLink>
        ) : (
          <Link to={homeHref} className='uni-nav-logo' aria-label='Home'>
            {logo}
          </Link>
        )}
      </div>
      <div className='uni-nav-right'>
        {!isArtifact && !isMobile && (
          <Link
            to='/memory'
            className='uni-nav-icon-btn uni-nav-labelled-btn'
            aria-label='Memories'
          >
            <MemoryIcon />
            <span className='uni-nav-btn-label'>Memories</span>
          </Link>
        )}
        {!isPublic &&
          (isArtifact ? (
            <NewWindowLink
              href='/settings'
              className='uni-nav-icon-btn'
              aria-label='Settings'
            >
              <SettingsIcon />
            </NewWindowLink>
          ) : (
            <Link
              to='/settings'
              state={settingsLinkState(location)}
              className={`uni-nav-icon-btn${isMobile ? '' : ' uni-nav-labelled-btn'}`}
              aria-label='Settings'
            >
              <SettingsIcon />
              {!isMobile && <span className='uni-nav-btn-label'>Settings</span>}
            </Link>
          ))}
        {isArtifact && !isMobile && (
          <NewWindowLink
            href={homeHref}
            className='uni-nav-icon-btn'
            aria-label='Back to Home'
          >
            <HomeIcon />
          </NewWindowLink>
        )}
        {!isPublic &&
          (isArtifact
            ? agentWhatsappLink && (
                <a
                  href={agentWhatsappLink}
                  target='_blank'
                  rel='noreferrer'
                  className='uni-nav-icon-btn uni-nav-whatsapp-btn'
                  aria-label='Chat on WhatsApp'
                >
                  <WhatsappIcon />
                </a>
              )
            : isMobile &&
              whatsappLink && (
                <a
                  href={whatsappLink}
                  target='_blank'
                  rel='noreferrer'
                  className='uni-nav-icon-btn uni-nav-whatsapp-btn'
                  aria-label='Chat on WhatsApp'
                >
                  <WhatsappIcon />
                </a>
              ))}
        {!isArtifact && !isMobile && (
          <button
            type='button'
            className='uni-nav-icon-btn'
            onClick={handleLogout}
            aria-label='Log out'
          >
            <LogoutIcon />
          </button>
        )}
      </div>
    </div>
  );
};

export default UniNav;
